import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import './DashboardLayout.css';
import VersionDisplay from '../VersionDisplay';
import AlertNotificationCenter from '../alerts/AlertNotificationCenter';
import LanguageSwitcher from '../LanguageSwitcher';
import OrgSwitcherBadge from '../ui/OrgSwitcherBadge';
import { APP_LOGO_SRC } from '../../constants/appLogo';

/**
 * Shared Dashboard Layout Component
 * Provides consistent header, sidebar, and layout structure for all role-specific dashboards
 */
const DashboardLayout = ({ 
  children, 
  userRole, 
  userName,
  userId,
  onLogout, 
  navigationItems = [],
  activeSection,
  onNavigate,
  showNotifications = true
}) => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [notificationCount, setNotificationCount] = useState(0);

  const getRoleDisplayName = (roleId) => {
    const roleNames = {
      'management': 'Management',
      'ic_personnel': 'IC Personnel',
      'administration': 'Administration',
      'execution': 'Execution',
      'prescriptors': 'Prescriptors'
    };
    return roleNames[roleId] || roleId;
  };

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  return (
    <div className="dashboard-layout">
      <div className="dashboard-body">
        {/* Sidebar */}
        <aside className={`dashboard-sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
          <div className="sidebar-header">
            <div className="logo-container">
              <img src={APP_LOGO_SRC} alt="ZeroPath" className="sidebar-logo" />
            </div>
            <LanguageSwitcher />
          </div>
          
          <nav className="menu">
            {navigationItems.map((item, index) => {
              if (item.type === 'divider') {
                return <div key={`divider-${index}`} style={{ height: '4px' }} />;
              }
              const itemKey = item.id ?? `nav-${index}`;
              if (item.type === 'section') {
                return (
                  <div key={itemKey} className="menu-section">
                    <button
                      className={`menu-parent ${item.expanded ? 'active' : ''}`}
                      onClick={() => {
                        if (item.children && item.children.length > 0) {
                          onNavigate(`toggle-${item.id}`);
                        }
                      }}
                    >
                      <span>{item.label}</span>
                      {item.children && item.children.length > 0 && (
                        <ChevronDown 
                          size={16} 
                          className="expand-icon"
                          style={{ 
                            transform: item.expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.2s ease'
                          }}
                        />
                      )}
                    </button>
                    {item.expanded && item.children && (
                      <div className="menu-children">
                        {item.children.map((child, childIndex) => (
                          <button
                            key={child.id ?? `child-${childIndex}`}
                            className={`menu-item ${activeSection === child.id ? 'active' : ''}`}
                            onClick={() => onNavigate(child.id)}
                          >
                            {child.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }
              return (
                <button
                  key={itemKey}
                  className={`menu-link ${activeSection === item.id ? 'active' : ''}`}
                  onClick={() => onNavigate(item.id)}
                >
                  {item.label}
                </button>
              );
            })}
          </nav>

          {/* Sidebar Footer */}
          <div className="sidebar-footer">
            <div className="sidebar-user-info">
              <OrgSwitcherBadge />
              <div className="sidebar-user-details">
                <div className="sidebar-user-name">{userName}</div>
                <div className="sidebar-user-role">{getRoleDisplayName(userRole)}</div>
              </div>
              <div className="sidebar-actions">
                {showNotifications && userId && (
                  <AlertNotificationCenter 
                    userId={userId}
                    userName={userName}
                    onViewAll={() => onNavigate('alert-management')}
                  />
                )}
                <button className="sidebar-logout-btn" onClick={onLogout}>Logout</button>
              </div>
            </div>
            <div className="dashboard-footer">
              <div className="footer-content">
                <span className="footer-text">© 2025 ZeroPath</span>
                <VersionDisplay compact={true} />
              </div>
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <main className="dashboard-main">
          {children}
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;

